import qs from 'query-string';
import { isAxiosError } from 'axios';
import { CategoryTreeRequestDto, CategoryTreeResponseDto } from '../../../types/category/tree';
import SuperFetchV2 from '../../superFetchV2';
import {
  CategoriesFindResponseBody,
  CategoriesProductsFindAllResponseBodyDto,
  SubCategoriesResponseBodyDto,
} from '../../../types/api/categories';
import { CategoriesRequestDto } from '../../../types/category';
import { SubCategoriesRequestDto } from '../../../types/category/sub';
import {
  BULK_UPLOAD_JOB_EXPIRED,
  BulkUploadAcceptedResponse,
  BulkUploadJobQueryData,
} from '../../../types/category/bulkUpload';
import {
  PRODUCT_CATEGORY_LINK_BULK_API_PATH,
  PRODUCT_CATEGORY_LINK_BULK_CSV_CONTENT_TYPE,
  PRODUCT_CATEGORY_LINK_BULK_EXPIRED_RECHECK_DELAY_MS,
  PRODUCT_CATEGORY_LINK_BULK_UPLOAD_TIMEOUT_MS,
} from '../../../constants/service/app/productCategoryLinkBulkConstants';
import { CategoryProductListRequest } from './type';

/** 잡 만료·타 어드민 잡 판별용 (types/common/http.ts 는 값을 export 하지 않아 지역 상수로 둔다) */
const NOT_FOUND_STATUS = 404;

const categoryApiV2 = {
  getCategoryTree: (option: CategoryTreeRequestDto): Promise<CategoryTreeResponseDto> =>
    SuperFetchV2.get(`/categories/tree?${qs.stringify(option, { skipNull: true })}`),
  getCategoryList: (option: CategoriesRequestDto): Promise<CategoriesFindResponseBody> =>
    SuperFetchV2.get(`/categories?${qs.stringify(option, { skipNull: true })}`),
  getSubCategoryList: (
    mainCategoryId: number,
    option: SubCategoriesRequestDto = {},
  ): Promise<SubCategoriesResponseBodyDto> =>
    SuperFetchV2.get(`/categories/${mainCategoryId}/sub-categories?${qs.stringify(option, { skipNull: true })}`),
  getProductList: (
    categoryId: number,
    option: CategoryProductListRequest,
  ): Promise<CategoriesProductsFindAllResponseBodyDto> =>
    SuperFetchV2.get(`/categories/${categoryId}/products?${qs.stringify(option, { skipNull: true })}`),

  /**
   * 상품-카테고리 링크 CSV 벌크 업로드 요청. 202 로 jobId 만 받고 처리는 워커가 한다.
   *
   * @param onUploadProgress - 전송률(0~100). 30MB 를 보내는 동안 화면에 표시하기 위함
   */
  uploadProductCategoryLinkBulk: (
    file: File,
    onUploadProgress?: (percent: number) => void,
  ): Promise<BulkUploadAcceptedResponse> => {
    const formData = new FormData();

    /**
     * 윈도우에서 엑셀이 저장한 .csv 는 브라우저가 application/vnd.ms-excel 로 보고하는
     * 경우가 있어 백엔드 mime 검증(text/csv)에 걸린다. 내용은 그대로 두고 타입만 지정해 감싼다.
     */
    formData.append('file', new File([file], file.name, { type: PRODUCT_CATEGORY_LINK_BULK_CSV_CONTENT_TYPE }));

    return SuperFetchV2.post(PRODUCT_CATEGORY_LINK_BULK_API_PATH.UPLOAD, formData, {
      // 진행률은 화면 카드가 직접 보여준다 — 전송 동안 어드민 전체를 로더로 덮지 않는다
      skipFullscreenLoader: true,
      // 400/409 는 호출부가 코드 매핑 토스트로 처리한다
      skipErrorModal: true,
      timeout: PRODUCT_CATEGORY_LINK_BULK_UPLOAD_TIMEOUT_MS,
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) {
          return;
        }
        onUploadProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
  },

  /**
   * 벌크 잡 상태 조회 (폴링).
   *
   * 404 는 예외로 던지지 않고 만료 상태로 변환한다. 완료 잡은 24시간 후 삭제되고 다른
   * 어드민의 잡도 404 로 오는데, 이는 폴링이 정상 종료해야 하는 상태다. 또 이 응답 바디에는
   * errors[] 가 없어서 예외로 흘리면 전역 QueryCache.onError 가
   * "Request failed with status code 404" 를 그대로 토스트한다.
   *
   * 단, 404 한 번으로 바로 만료 처리하면 일시적 404(게이트웨이 blip, Redis failover 중
   * getJob null 등)에 진행 카드와 저장 기록을 영구히 잃는다 — 만료는 resolve 로 변환돼
   * 쿼리의 retry 가 적용되지 않기 때문. 그래서 잠시 뒤 한 번 재확인하고, 연속 404 일
   * 때만 만료로 확정한다. 실제로 만료된 잡은 재확인도 404 라 요청 1회만 더 든다.
   */
  getProductCategoryLinkBulkJob: async (jobId: string): Promise<BulkUploadJobQueryData> => {
    const fetchJob = (): Promise<BulkUploadJobQueryData> =>
      SuperFetchV2.get(PRODUCT_CATEGORY_LINK_BULK_API_PATH.JOB(jobId), {
        skipFullscreenLoader: true,
        skipErrorModal: true,
      });
    const isNotFound = (error: unknown): boolean =>
      isAxiosError(error) && error.response?.status === NOT_FOUND_STATUS;

    try {
      return await fetchJob();
    } catch (error) {
      if (!isNotFound(error)) {
        throw error;
      }
    }

    await new Promise((resolve) => {
      setTimeout(resolve, PRODUCT_CATEGORY_LINK_BULK_EXPIRED_RECHECK_DELAY_MS);
    });

    try {
      return await fetchJob();
    } catch (error) {
      if (isNotFound(error)) {
        return { status: BULK_UPLOAD_JOB_EXPIRED };
      }
      throw error;
    }
  },

  /**
   * 실패 행 CSV 다운로드. signed URL 이 아니라 Admin API 가 S3 를 프록시해 스트림으로 내려준다.
   * 응답 인터셉터가 res.data 만 돌려주므로 Content-Disposition 은 읽을 수 없고,
   * 파일명은 호출부가 jobId 로 만든다.
   */
  downloadProductCategoryLinkBulkFailedCsv: (jobId: string): Promise<Blob> =>
    SuperFetchV2.get(PRODUCT_CATEGORY_LINK_BULK_API_PATH.FAILED_CSV(jobId), {
      responseType: 'blob',
      // 카드의 다운로드 버튼이 자체 로딩 상태('다운로드 중...')를 보여준다 — 큰 CSV 를 받는
      // 동안 전역 로더로 화면 전체를 덮지 않는다 (실패 시 fetchRequestList.reset 이 다른
      // in-flight 요청 추적을 지우는 것도 피한다)
      skipFullscreenLoader: true,
      skipErrorModal: true,
    }),
};

export default categoryApiV2;
